<script lang="ts">
  import { stringToSlug } from '@24hbs/utils';
  import { getNext12MonthsRanges } from '$lib/utils/date';
  import { LESSION_MODES, STUDENT_TYPES } from '$lib/utils/filters/constants';
  import { toArray } from '$lib/utils/filters/helpers';
  import type { FilterType, IFilters } from '$lib/utils/filters/types';
  import { FilterStore, getDefaultFilters } from '$lib/utils/filters/store';
  import AppliedFiltersWrapper from './AppliedFiltersWrapper.svelte';
  import Filter from './Filter.svelte';
  import FilterDropdown from './FilterDropdown.svelte';
  import StickyFilters from './StickyFilters.svelte';
  import { GtmStore } from '$lib/utils/gtm-store';
	import { goto } from '$app/navigation';
	import { Checkbox, CompanyFlag, RadioButton, Range } from '$lib/components/atoms/Field';
	import { ReviewScore } from '$lib/components/atoms/ReviewScore';
	import { Grid } from '$lib/components/organisms/Layout';
	import { ChipButton } from '$lib/components/atoms/Button';
	import { LinkAction } from '$lib/components/atoms/Link';
	import { Loading } from '$lib/components/atoms/Loading';
	import { page } from '$app/stores';
	import { useCourseCategoryUtils } from '$lib/utils/courses/category-utils';
	import type { ICourseCategoryFields, ICourseTypeFields } from '$lib/utils/contentful/types';
	import { onMount } from 'svelte';
	import { useCourseTypeUtils } from '$lib/utils/courses/type-utils';
	import { useSearch } from '$lib/utils/search';

  export let showCourseType: boolean = false;
  export let showScore: boolean = false;
  export let showCategory: boolean = true;
  export let showLessonMode: boolean = true;
  export let showDuration: boolean = false;
  export let showMonth: boolean = true;
  export let showPrice: boolean = true;
  export let showStudentType: boolean = true;
  export let showCreditsFilter: boolean = true;
  export let loading: boolean = false;
  export let stickTo: string = 'header';
  export let triggerHideStickyFilters: HTMLElement | null = null;
  export let preloadedFilters: IFilters | null = null;

  const filterStoreInstance = FilterStore.getInstance();
  const filters = filterStoreInstance.filterStore;
  const search = useSearch(fetch);

  if (preloadedFilters) {
    filterStoreInstance.setPreloadedFilters(preloadedFilters);
  }

  const filtersApplied = filterStoreInstance.filtersApplied;
  const isAnyFilterApplied = filterStoreInstance.isAnyFilterApplied;

  const courseTypeFacets = filterStoreInstance.courseTypeFacets;
  const categoryFacets = filterStoreInstance.categoryFacets;
  const lessionModeFacets = filterStoreInstance.lessionModeFacets;
  const durationFacets = filterStoreInstance.durationFacets;
  const studentTypeFacets = filterStoreInstance.studentTypeFacets;
  const monthFacets = filterStoreInstance.monthFacets;
  const creditsFacets = filterStoreInstance.creditsFacets;

  const MONTHS_YEARS = getNext12MonthsRanges();

  // call gtm event when filters are applied
  const pushGtmEvent = (filter: string, values: string) => {
    GtmStore.pushEvent(
      {
        event: 'BBevent',
        event_id: '50',
        category: 'Filtri',
        action: filter,
        label: values
      },
      false
    );
  };

  const onFilterChange = (e) => {
    const newFilters = { ...$filters };

    const selectedFilters = e.detail.data;

    if (selectedFilters.tipologia) {
      newFilters.courseType = toArray(selectedFilters.tipologia);
      pushGtmEvent('Tipologia', newFilters.courseType.join(', '));
    } else {
      newFilters.courseType = [];
      pushGtmEvent('Tipologia', '');
    }

    if (selectedFilters.category) {
      newFilters.category = toArray(selectedFilters.category);
      pushGtmEvent('Tematiche', newFilters.category.join(' - '));
    } else {
      newFilters.category = [];
      pushGtmEvent('Tematiche', '');
    }
    if (selectedFilters.lessionMode) {
      newFilters.lessionMode = toArray(selectedFilters.lessionMode);
      pushGtmEvent('Fruizione', newFilters.lessionMode.join(', '));
    } else {
      newFilters.lessionMode = [];
      pushGtmEvent('Fruizione', '');
    }

    if (selectedFilters.studentType) {
      newFilters.studentType = toArray(selectedFilters.studentType);
      pushGtmEvent('Tipo Partecipante', newFilters.studentType.join(', '));
    } else {
      newFilters.studentType = [];
      pushGtmEvent('Tipo Partecipante', '');
    }

    if (selectedFilters.duration) {
      newFilters.duration = toArray(selectedFilters.duration);
      pushGtmEvent('Durata', newFilters.duration.join(', '));
    } else {
      newFilters.duration = [];
    }

    let priceFilter = '';
    if (selectedFilters.priceMin) {
      newFilters.priceMin = selectedFilters.priceMin;
      priceFilter = `${priceFormat(selectedFilters.priceMin)}`;
    } else {
      priceFilter = `${priceFormat(0)}`;
    }
    if (selectedFilters.priceMax) {
      newFilters.priceMax = selectedFilters.priceMax;
      priceFilter = `${priceFilter}-${priceFormat(selectedFilters.priceMax)}`;
    } else {
      priceFilter = `${priceFilter}-${priceFormat(30000)}`;
    }
    if (priceFilter) {
      pushGtmEvent('Prezzo', priceFilter);
    }


    if (selectedFilters.valutazione) {
      newFilters.score = selectedFilters.valutazione;
      pushGtmEvent('Valutazione', newFilters.score.toString());
    }

    if (selectedFilters.month) {
      newFilters.month = toArray(selectedFilters.month);
      pushGtmEvent('Data Partenza', newFilters.month.join(', '));
    } else {
      newFilters.month = [];
      pushGtmEvent('Data Partenza', '');
    }

    if (selectedFilters.credits) {
      newFilters.credits = selectedFilters.credits;
      pushGtmEvent('Certificazioni', newFilters.credits);
    }

    filters.update((currentFilters) => {
      return {
        ...currentFilters,
        ...newFilters,
      };
    });

    applyQuery();
  };

  $: durationFilterText = $filters.duration.map((duration) => ' ' + duration + ' mesi').join(',');

  const priceFormat = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      currency: 'EUR',
    }).format(value);
  };

  const removeFilter = (filter: FilterType, value?: number | string) => {
    const defaultFilters = getDefaultFilters();

    filters.update((currentValue) => {
      switch (filter) {
        case 'priceMax':
        case 'priceMin':
        case 'score':
        case 'credits':
          return {
            ...currentValue,
            [filter]: defaultFilters[filter],
          };
        case 'courseType':
        case 'category':
        case 'lessionMode':
        case 'duration':
        case 'studentType':
        case 'month':
          return {
            ...currentValue,
            [filter]: (currentValue[filter] as (number | string)[]).filter((f) => f !== value),
          };
      }
    });

    applyQuery();
  };

  const applyQuery = () => {
    let baseUrl = new URL(document.location.href).pathname;
    if ($page.route.id === '/(app)/esplora') {
      baseUrl = '/esplora';
    }
    const searchParams = new URL(document.location.href).searchParams;

    searchParams.delete('prod');
    searchParams.delete('loc');
    searchParams.delete('at');
    searchParams.delete('s');
    if ($page.route.id !== '/(app)/esplora') {
      searchParams.delete('q');
    }

    for (let filter in $filters) {
      if ($page.route.id === '/(app)/esplora' && $filtersApplied[filter] || $filtersApplied[filter] && preloadedFilters?.[filter]?.length === 0) {
        if (Array.isArray($filters[filter])) {
          searchParams.set(filter, $filters[filter].join(';'));
        } else {
          searchParams.set(filter, $filters[filter]);
        }
      } else {
        searchParams.delete(filter);
      }
    }

    const queryString = searchParams.toString() !== '' ? '?' + searchParams.toString() : '';

    const url = baseUrl + queryString;

    goto(url, { noScroll: true });
  };

  const removeAllFilters = () => {
    filters.set(getDefaultFilters());
    applyQuery();
  };

  let courseTypeText = '';
  let lessionModeText = '';
  let studentTypeText = '';

  const getFilterText = (filterValue: any[] | any, filterOptions: any[]) => {
    if (!Array.isArray(filterValue)) return filterOptions.find((option) => option.value === filterValue)?.text || '';

    if (filterValue.length > 0) {
      return filterValue
        .map((value) => {
          return filterOptions.find((option) => option.value === value)?.text || '';
        })
        .join(',');
    }
    return '';
  };

  const getAllCourseCategories = async () => {
    const courseCategoryUtils = useCourseCategoryUtils(fetch);
    const courseCategories: ICourseCategoryFields[] = await courseCategoryUtils.getAllCourseCategories();
    return courseCategories.map((category: ICourseCategoryFields) => {
      return {
        value: category.slug,
        text: category.categoryName,
      };
    });
  };

  const getAllCourseTypes = async () => {
    const courseTypeUtils = useCourseTypeUtils(fetch);
    const courseTypes: ICourseTypeFields[] = await courseTypeUtils.getAllCourseTypes();
    return courseTypes.map((type: ICourseTypeFields) => {
      return {
        value: type.slug,
        text: type.typeName,
      };
    });
  };

  let isLoadingFacets = true;

  let initialFiltersOptions = {
    courseTypes: {},
    categories: {},
    lessionModes: {},
    // durations: {},
    studentTypes: {},
    months: {},
    credits: {},
  };

  let initialFacetsVisibility = {
    studentType: false,
    courseType: false,
    category: false,
    lessionMode: false,
    duration: false,
    month: false,
    credits: false,
  }

  let COURSE_CATEGORIES: { value: string; text: string }[] = [];
  let COURSE_TYPES: { value: string; text: string }[] = [];

  onMount(async () => {
    COURSE_CATEGORIES = await getAllCourseCategories();
    COURSE_TYPES = await getAllCourseTypes();

    // Set the initial filters options
    initialFiltersOptions.studentTypes = STUDENT_TYPES.filter((studentType) => {
      if ($studentTypeFacets && $studentTypeFacets[stringToSlug(studentType.value)] > 0) {
        return studentType;
      }
    });
    initialFiltersOptions.courseTypes = COURSE_TYPES.filter((courseType) => {
      if ($courseTypeFacets && $courseTypeFacets[stringToSlug(courseType.value)] > 0) {
        return courseType;
      }
    });
    initialFiltersOptions.categories = COURSE_CATEGORIES.filter((category) => {
      if ($categoryFacets && $categoryFacets[stringToSlug(category.value)] > 0) {
        return category;
      }
    });
    initialFiltersOptions.lessionModes = LESSION_MODES.filter((lessionMode) => {
      if ($lessionModeFacets && $lessionModeFacets[stringToSlug(lessionMode.value)] > 0) {
        return lessionMode;
      }
    });
    initialFiltersOptions.months = MONTHS_YEARS.filter((monthYear) => {
      if ($monthFacets && $monthFacets[stringToSlug(monthYear.value)] > 0) {
        return monthYear;
      }
    });
    initialFiltersOptions.credits = [
      { value: 'cfu', text: 'CFU' },
      { value: 'cfp', text: 'CFP' },
    ].filter((credits) => {
      if ($creditsFacets && $creditsFacets[stringToSlug(credits.value)] > 0) {
        return credits;
      }
    });

    // Memorize the initial count of facets
    initialFacetsVisibility = {
      studentType: FilterStore.countFacets($studentTypeFacets) > 1,
      courseType: FilterStore.countFacets($courseTypeFacets) > 1,
      category: FilterStore.countFacets($categoryFacets) > 1,
      lessionMode: FilterStore.countFacets($lessionModeFacets) > 1,
      duration: FilterStore.countFacets($durationFacets) > 1,
      month: FilterStore.countFacets($monthFacets) > 1,
      credits: FilterStore.countFacets($creditsFacets) > 1,
    }

    isLoadingFacets = false;
  });

  // These values are reactive
  $: courseTypeText = getFilterText($filters.courseType, COURSE_TYPES);
  $: lessionModeText = getFilterText($filters.lessionMode, LESSION_MODES);
  $: studentTypeText = getFilterText($filters.studentType, STUDENT_TYPES);
  $: categoryText = getFilterText($filters.category, COURSE_CATEGORIES);
  $: monthText = getFilterText($filters.month, MONTHS_YEARS);
</script>

<StickyFilters {stickTo} {triggerHideStickyFilters}>
  <Filter on:removeAllFilters={removeAllFilters} on:change={onFilterChange}>
    {#if isLoadingFacets}
      <div class="filter-skeleton"></div>
    {:else if showStudentType && initialFacetsVisibility.studentType}
      <FilterDropdown title="Tipo Partecipante" currentValue={studentTypeText}>
        {#each initialFiltersOptions.studentTypes as studentType, index (studentType.value)}
          <Checkbox
            id="studentType-{index + 1}"
            name="studentType"
            value={studentType.value}
            checked={$filters.studentType?.includes(studentType.value)}
          >
            <div class="flex flex-1 justify-between">
              <span>{studentType.text}</span>
            </div>
          </Checkbox>
        {/each}
      </FilterDropdown>
    {/if}
    {#if isLoadingFacets}
      <div class="filter-skeleton"></div>
    {:else if showCourseType && initialFacetsVisibility.courseType}
      <FilterDropdown title="Tipologia" currentValue={courseTypeText}>
        {#each initialFiltersOptions.courseTypes as courseType, index (courseType.value)}
          <Checkbox
            id="tipologia-{index + 1}"
            name="tipologia"
            value={courseType.value}
            checked={$filters.courseType?.includes(courseType.value)}
          >
            <div class="flex flex-1 justify-between">
              <span>{courseType.text}</span>
            </div>
          </Checkbox>
        {/each}
      </FilterDropdown>
    {/if}

    {#if !isLoadingFacets && showScore}
      <FilterDropdown title="Valutazioni" currentValue={$filters.score ? $filters.score + ' Stelle' : null}>
        {#each [5, 4, 3, 2, 1] as score}
          <RadioButton
            value={score.toString()}
            id="valutazione-{score}"
            name="valutazione"
            bind:group={$filters.score}
          >
            <ReviewScore {score} />
          </RadioButton>
        {/each}
      </FilterDropdown>
    {/if}

    {#if isLoadingFacets}
      <div class="filter-skeleton"></div>
    {:else if showCategory && initialFacetsVisibility.category}
      <FilterDropdown title="Tematiche" currentValue={categoryText}>
        {#each initialFiltersOptions.categories as category, index (category)}
          <Checkbox
            id="tema-{index + 1}"
            name="category"
            value={category.value}
            checked={$filters.category?.includes(category.value)}
          >
            <div class="flex flex-1 justify-between">
              <div>{category.text}</div>
            </div>
          </Checkbox>
        {/each}
      </FilterDropdown>
    {/if}

    {#if isLoadingFacets}
      <div class="filter-skeleton"></div>
    {:else if showLessonMode && initialFacetsVisibility.lessionMode}
      <FilterDropdown title="Fruizione" currentValue={lessionModeText}>
        {#each initialFiltersOptions.lessionModes as lessionMode, index (lessionMode.value)}
          <Checkbox
            id="funz-{index + 1}"
            name="lessionMode"
            value={lessionMode.value}
            checked={$filters.lessionMode?.includes(lessionMode.value)}
          >
            <div class="flex flex-1 justify-between">
              <span>{lessionMode.text}</span>
            </div>
          </Checkbox>
        {/each}
      </FilterDropdown>
    {/if}

    {#if isLoadingFacets}
      <div class="filter-skeleton"></div>
    {:else if showDuration && initialFacetsVisibility.duration}
      <!-- FIXME: Check if the duration filter are still needed -->
      <FilterDropdown title="Durata" currentValue={durationFilterText}>
        <Checkbox
          id="dur-1"
          name="duration"
          value="9"
          checked={$filters.duration?.includes(9)}
        >
          <div class="flex flex-1 justify-between">
            <span>9 mesi</span>
          </div>
        </Checkbox>
        <Checkbox
          id="dur-2"
          name="duration"
          value="12"
          checked={$filters.duration?.includes(12)}
        >
          <div class="flex flex-1 justify-between">
            <span>12 mesi</span>
          </div>
        </Checkbox>
        <Checkbox
          id="dur-3"
          name="duration"
          value="24"
          checked={$filters.duration?.includes(24)}
        >
          <div class="flex flex-1 justify-between">
            <span>24 mesi</span>
          </div>
        </Checkbox>
      </FilterDropdown>
    {/if}

    {#if !isLoadingFacets && showMonth && initialFacetsVisibility.month}
      <FilterDropdown title="Data Partenza" currentValue={monthText}>
        {#each initialFiltersOptions.months as monthYear, index}
          <Checkbox
            id="month-{index}"
            name="month"
            value={monthYear.value}
            checked={$filters.month?.includes(monthYear.value)}
          >
            <div class="flex flex-1 justify-between">
              <span>{monthYear.text}</span>
            </div>
          </Checkbox>
        {/each}
      </FilterDropdown>
    {/if}

    {#if !isLoadingFacets && showPrice}
      <FilterDropdown
        title="Prezzo"
        currentValue={$filtersApplied.priceMin || $filtersApplied.priceMax
          ? `DA ${priceFormat($filters.priceMin)} A ${priceFormat($filters.priceMax)}`
          : ''}
      >
        <Range
          id="prez"
          minName="priceMin"
          maxName="priceMax"
          minText="Minimo"
          maxText="Massimo"
          min={0}
          max={30000}
          step={100}
          currentMaxValue={$filters.priceMax}
          currentMinValue={$filters.priceMin}
          formatter={priceFormat}
        />
      </FilterDropdown>
    {/if}

    {#if !isLoadingFacets && showCreditsFilter && initialFacetsVisibility.credits}
      <FilterDropdown title="Certificazioni" currentValue={$filters.credits?.toUpperCase() || undefined}>
        {#each initialFiltersOptions.credits as credit, index}
          <RadioButton
            id="credits-{index + 1}"
            name="credits"
            value={credit.value}
            bind:group={$filters.credits}
          >
            <div class="flex flex-1 justify-between">
              <span>{credit.text}</span>
            </div>
          </RadioButton>
        {/each}
      </FilterDropdown>
    {/if}
  </Filter>

  {#if $isAnyFilterApplied && !isLoadingFacets}
    <Grid noGrid>
      <AppliedFiltersWrapper>
        {#if !preloadedFilters?.studentType?.length}
          {#each $filters.studentType as studentType}
            <ChipButton value={studentType} on:removefilter={() => removeFilter('studentType', studentType)} />
          {/each}
        {/if}

        {#if !preloadedFilters?.courseType?.length}
          {#each $filters.courseType as type (type)}
            <ChipButton value={getFilterText(type, COURSE_TYPES)} on:removefilter={() => removeFilter('courseType', type)} />
          {/each}
        {/if}

        {#if !preloadedFilters?.score}
          {#if $filtersApplied.score}
            <ChipButton value="{$filters.score} Stelle" on:removefilter={() => removeFilter('score')} />
          {/if}
        {/if}

        {#if !preloadedFilters?.category?.length}
          {#each $filters.category as category}
            <ChipButton
              value={getFilterText(category, COURSE_CATEGORIES)}
              on:removefilter={() => removeFilter('category', category)}
            />
          {/each}
        {/if}

        {#if !preloadedFilters?.lessionMode?.length}
          {#each $filters.lessionMode as lessionMode}
            <ChipButton
              value={getFilterText(lessionMode, LESSION_MODES)}
              on:removefilter={() => removeFilter('lessionMode', lessionMode)}
            />
          {/each}
        {/if}

        {#if !preloadedFilters?.duration?.length}
          {#each $filters.duration as duration}
            <ChipButton value="{duration} mesi" on:removefilter={() => removeFilter('duration', duration)} />
          {/each}
        {/if}

        {#if !preloadedFilters?.month?.length}
          {#each $filters.month as month (month)}
            <ChipButton value={getFilterText(month, MONTHS_YEARS)} on:removefilter={() => removeFilter('month', month)} />
          {/each}
        {/if}

        {#if !preloadedFilters?.priceMin && !preloadedFilters?.priceMax}
          {#if $filtersApplied.priceMin || $filtersApplied.priceMax}
            <ChipButton
              value={`DA ${priceFormat($filters.priceMin)} A ${priceFormat($filters.priceMax)}`}
              on:removefilter={() => {
                removeFilter('priceMin');
                removeFilter('priceMax');
              }}
            />
          {/if}
        {/if}

        {#if !preloadedFilters?.credits}
          {#if $filtersApplied.credits && showCreditsFilter}
            <ChipButton value={$filters.credits} on:removefilter={() => removeFilter('credits')} />
          {/if}
        {/if}

        <div style="display: flex;" slot="action">
          {#if $isAnyFilterApplied}
            <LinkAction iconPos="end" icon="reset" on:click={() => removeAllFilters()}>Rimuovi filtri</LinkAction>
          {/if}
        </div>
      </AppliedFiltersWrapper>
    </Grid>
  {/if}
  <!-- <div class="pt-6">
    <CompanyFlag />
  </div> -->
</StickyFilters>

<Loading {loading} />

<style scoped type="text/scss" lang="scss">
  .facet {
    display: inline-flex;
    background-color: #e5e5e5;
    height: 25px;
    width: 25px;
    flex: 25px 0 0;
    font-size: 12px;
    outline: none;
    font-color: #333333;
    border-radius: 16px;
    align-items: center;
    justify-content: center;
  }

  .filter-skeleton {
    @apply w-full;
    @apply bg-gray-200;
    @apply rounded;
    @apply animate-pulse;

    height: 60px;
  }
</style>
